import { computed, type MaybeRefOrGetter, toValue } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { http, isApiError, unwrap } from './client'
import { workspacePath } from './workspaces'
import type { ApiEnvelope } from '@/types/api'
import type { ClinicalIndicatorId, ContinuityMetricId, InstrumentationGap, JourneyStage, MetricReadiness } from '@/types/intelligence'
import { keptWithin, type BlockedReason, type MetricChange, type OperationsSnapshot, type PeriodSelection } from './snapshot'

/** What limits a journey measure, for the screen to put into words (P2 §3.8). */
export type ContinuityNote =
  | { code: BlockedReason }
  /** ElfieCare's clinical records aren't shared with Intelligence yet (DECISIONS.md #39). */
  | { code: 'RECORDS_NOT_CONNECTED' }
  /** The workspace records none of these yet: `unlock` has both ways to get each. */
  | { code: 'INPUTS_NOT_RECORDED'; inputs: string[] }
  /** Fewer than a figure may describe, never saying how many (S0 §17.1). */
  | { code: 'TOO_FEW'; minimum: number }
  | { code: 'NOT_RECORDED_BEFORE'; day: string }
  | { code: 'NOT_RECORDED'; day: string | null }
  | { code: 'LAB_ORDERS_NOT_RECORDED' }
  | { code: 'MEASURED_OF'; measured: number; of: number }

/** One journey measure or guideline indicator, taken at the period's last moment and at its baseline's. */
export interface ContinuityMeasure {
  metricId: ContinuityMetricId | ClinicalIndicatorId
  unit: 'PATIENTS' | 'RESULTS' | 'CARE_LOOPS' | 'PATIENT_MONTHS'
  calculationRule: 'RATE' | 'COUNT'
  readiness: MetricReadiness
  /** Null unless the readiness allows a value: never a zero for "unknown". */
  numerator: number | null
  denominator: number | null
  /** 0 to 1; null for a count. */
  rate: number | null
  baselineNumerator: number | null
  baselineDenominator: number | null
  baselineRate: number | null
  deltaPercentagePoints: number | null
  /** For a rate, in percentage points; for a count, in its unit. */
  change: MetricChange | null
  comparable: boolean
  notes: ContinuityNote[]
  unlock: InstrumentationGap[]
  betterWhen: 'HIGHER'
}

export interface JourneyStageSnapshot extends ContinuityMeasure {
  metricId: ContinuityMetricId
  stage: JourneyStage
  stageNumber: 1 | 2 | 3 | 4 | 5
  /** The stage's other measure, under the card's lead. */
  detail: ContinuityMeasure & { metricId: ContinuityMetricId }
}

export interface ClinicalIndicatorSnapshot extends ContinuityMeasure {
  metricId: ClinicalIndicatorId
  guideline: 'ADA' | 'AHA_ACC' | 'KDIGO' | 'WORKFLOW'
}

/** The Care Quality & Continuity perspective (P2 §2.2): five stages, never one score, and the guideline indicators. */
export interface ContinuitySnapshot {
  period: OperationsSnapshot['period']
  /** The moment every measure is taken at, and the one it is compared with. */
  asOf: string
  baselineAsOf: string
  /** Whether the period runs on past `asOf`, today being in it: its figures are as of now, not as of its end. */
  ongoing: boolean
  dataHealth: OperationsSnapshot['dataHealth']
  /** Whether ElfieCare's clinical records can be read; null while the workspace's data cannot be read at all. */
  recordsConnected: boolean | null
  recordsFrom: string | null
  stages: JourneyStageSnapshot[]
  clinicalIndicators: ClinicalIndicatorSnapshot[]
  /** Every input the workspace records none of yet, each with both ways to get it: the coverage story. */
  missingInputs: InstrumentationGap[]
}

export const continuityKeys = {
  snapshot: (workspaceId: string, selection: PeriodSelection) =>
    ['workspaces', workspaceId, 'snapshot', 'continuity', selection.period, selection.from ?? '', selection.to ?? ''] as const,
}

/** Figures move as records arrive: read again each minute while the page is open, as the Operations snapshot is. */
const REFRESH_MS = 60_000

/** The Care Quality & Continuity snapshot (P2 §3.2), for the period on screen. */
export const useContinuitySnapshot = (
  workspaceId: MaybeRefOrGetter<string>,
  selection: MaybeRefOrGetter<PeriodSelection>,
  enabled: MaybeRefOrGetter<boolean> = true,
) =>
  useQuery({
    queryKey: computed(() => continuityKeys.snapshot(toValue(workspaceId), toValue(selection))),
    queryFn: async () => {
      const { period, from, to } = toValue(selection)
      const params = period === 'CUSTOM' ? { period, from, to } : { period }
      return unwrap(
        (await http.get<ApiEnvelope<ContinuitySnapshot>>(`${workspacePath(toValue(workspaceId))}/snapshot/continuity`, { params }))
          .data,
      )
    },
    enabled: computed(() => toValue(enabled)),
    // As the Operations snapshot does: the last figures stay while a new period loads, never under another workspace's name.
    placeholderData: keptWithin(workspaceId),
    refetchInterval: REFRESH_MS,
    retry: (failures, error) => !(isApiError(error) && [400, 403, 404].includes(error.statusCode)) && failures < 1,
  })
